import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { MinistriesOverview, WorshipService } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { audienceLabel, ConvocationFields, type Audience } from '../../shared/ui/ConvocationFields';
import { SearchSelect } from '../../shared/ui/SearchSelect';
import { useAsync } from '../../shared/ui/useAsync';
import { formatDayHeading, formatTime, toLocalInput } from '../calendar/dates';
import { useMe } from '../session/SessionProvider';
import { SetlistEditor } from '../songs/ServiceSongsPage';

type Ref = { personId: string; name: string };
type Rehearsal = {
  id: string;
  seriesId: string | null;
  title: string;
  startsAt: string;
  endsAt: string;
  place: string | null;
  notes: string | null;
  service: { id: string; title: string; startsAt: string } | null;
  audience: Audience;
  convoked: Ref[];
  chosen: string[];
  canceled: boolean;
  songCount: number;
};
type RehearsalSong = { songId: string; title: string; key: string | null; notes: string | null; lyrics: string; author: string | null };
type RehearsalDetail = Rehearsal & { songs: RehearsalSong[]; canManage: boolean; people: Ref[] };

const WEEKDAYS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

const when = (r: { startsAt: string; endsAt: string }) => `${formatDayHeading(new Date(r.startsAt))} · ${formatTime(r.startsAt)} a ${formatTime(r.endsAt)}`;

/**
 * ¿Mostrar «Ensayos»? Solo a quien le sirve: la administración de cultos, quien organiza los ensayos (líder de
 * Alabanza), quien integra Alabanza y quien está convocado a algún ensayo próximo (p. ej. uno para toda la iglesia).
 */
export function useShowRehearsals(churchId: string | null): boolean {
  const me = useMe();
  const manage = me.can('worship.manage');
  const data = useAsync(
    async (signal) => {
      if (!churchId || manage) return manage;
      const [list, ministries] = await Promise.all([
        api.get<{ items: unknown[]; canManage: boolean }>(`/worship/churches/${churchId}/rehearsals`, signal).catch(() => null),
        api.get<MinistriesOverview>(`/organization/churches/${churchId}/ministries`, signal).catch(() => null),
      ]);
      return Boolean(list?.canManage || list?.items.length || ministries?.items.some((m) => m.systemKey === 'alabanza' && m.mine));
    },
    [churchId, manage],
  );
  return manage || (data.status === 'ready' && data.data);
}

/** Ensayos de Alabanza: cada quien ve los suyos; el líder (o la administración) los organiza. */
export function RehearsalsPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const data = useAsync(
    (signal) => (churchId ? api.get<{ items: Rehearsal[]; canManage: boolean; people: Ref[] }>(`/worship/churches/${churchId}/rehearsals`, signal) : Promise.resolve(null)),
    [churchId],
  );
  const [adding, setAdding] = useState(false);
  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;
  if (data.status === 'loading') return <div className="page"><Loading /></div>;
  if (data.status === 'error') return <div className="page"><ErrorState error={data.error} onRetry={data.reload} /></div>;
  const d = data.data;
  if (!d) return null;

  return (
    <div className="page">
      <p className="small">
        <Link to="/app/cultos">← Cultos</Link>
      </p>
      <header className="page__header page__header--actions">
        <div>
          <h1>Ensayos de Alabanza</h1>
          <p className="muted">{d.canManage ? 'Todos los ensayos que vienen.' : 'Los ensayos a los que estás convocado.'}</p>
        </div>
        {d.canManage && !adding ? <Button onClick={() => setAdding(true)}>Nuevo ensayo</Button> : null}
      </header>

      {adding ? <RehearsalForm churchId={churchId} people={d.people} onCancel={() => setAdding(false)} /> : null}

      <Card>
        {d.items.length === 0 ? (
          <EmptyState title="No hay ensayos programados.">{d.canManage ? <p className="muted">Crea uno, o uno cada semana con «Repetir cada semana».</p> : null}</EmptyState>
        ) : (
          <ul className="list">
            {d.items.map((r) => (
              <li key={r.id} className="list__item">
                <Link className="list__link list__link--row service-line" to={`/app/ensayos/${r.id}`}>
                  <span className="service-line__when">
                    {formatDayHeading(new Date(r.startsAt))} · {formatTime(r.startsAt)}
                  </span>
                  <span className="service-line__title">
                    <strong>{r.title}</strong>
                    <span className="small muted">
                      {r.place ? ` · ${r.place}` : ''} · {audienceLabel(r.audience, 'Alabanza', r.convoked.length)}
                      {r.songCount ? ` · ${r.songCount} ${r.songCount === 1 ? 'canto' : 'cantos'}` : ''}
                      {r.service ? ` · para «${r.service.title}»` : ''}
                    </span>
                  </span>
                  {r.canceled ? <span className="badge badge--warn">Cancelado</span> : null}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

/** Crear (una vez o cada semana) o editar un ensayo. */
function RehearsalForm({ churchId, people, rehearsal, onCancel, onSaved }: { churchId: string; people: Ref[]; rehearsal?: RehearsalDetail; onCancel: () => void; onSaved?: () => void }) {
  const navigate = useNavigate();
  const services = useAsync((signal) => api.get<{ items: WorshipService[] }>(`/worship/churches/${churchId}/services`, signal).then((r) => r.items.filter((s) => s.status === 'planned')), [churchId]);
  const [first, setFirst] = useState(() => {
    if (rehearsal) return toLocalInput(new Date(rehearsal.startsAt));
    const d = new Date(Date.now() + 2 * 86400_000);
    d.setHours(19, 0, 0, 0);
    return toLocalInput(d);
  });
  const [repeat, setRepeat] = useState(false);
  const [weeks, setWeeks] = useState(4);
  const [convocation, setConvocation] = useState<{ audience: Audience; chosen: string[] }>({ audience: rehearsal?.audience ?? 'ministerio', chosen: rehearsal?.chosen ?? [] });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const start = new Date(first);
  const minutesDefault = rehearsal ? Math.round((new Date(rehearsal.endsAt).getTime() - new Date(rehearsal.startsAt).getTime()) / 60_000) : 120;
  const count = Math.min(Math.max(weeks, 1), 60);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const t = (k: string) => String(f.get(k) ?? '').trim() || null;
    const minutes = Number(f.get('minutes') || 120);
    if (Number.isNaN(start.getTime())) return setError('Indica la fecha y hora.');
    const dateAt = (i: number) => {
      const s = new Date(start);
      s.setDate(s.getDate() + i * 7);
      return { startsAt: s.toISOString(), endsAt: new Date(s.getTime() + minutes * 60_000).toISOString() };
    };
    const fields = { title: t('title'), place: t('place'), notes: t('notes'), serviceId: repeat ? null : t('serviceId'), audience: convocation.audience, people: convocation.chosen };
    setBusy(true);
    setError(null);
    try {
      if (rehearsal) {
        await api.put(`/worship/churches/${churchId}/rehearsals/${rehearsal.id}`, { ...fields, ...dateAt(0) });
        onSaved?.();
      } else {
        const dates = Array.from({ length: repeat ? count : 1 }, (_, i) => dateAt(i));
        const { ids } = await api.post<{ ids: string[] }>(`/worship/churches/${churchId}/rehearsals`, { ...fields, dates });
        navigate(`/app/ensayos/${ids[0]}`);
      }
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <Card title={rehearsal ? 'Editar ensayo' : 'Nuevo ensayo'}>
      <form className="form" onSubmit={onSubmit} noValidate>
        {error ? <Alert>{error}</Alert> : null}
        <Field label="Nombre (opcional)" name="title" maxLength={120} defaultValue={rehearsal && rehearsal.title !== 'Ensayo de Alabanza' ? rehearsal.title : ''} placeholder="Ensayo de Alabanza" />
        <div className="form__row">
          <Field label={repeat ? 'Primer ensayo' : 'Fecha y hora'} type="datetime-local" value={first} onChange={(e) => setFirst(e.target.value)} required />
          <Field label="Duración (minutos)" name="minutes" type="number" min={15} max={720} defaultValue={minutesDefault} />
        </div>
        {!rehearsal ? (
          <>
            <label className="check">
              <input type="checkbox" checked={repeat} onChange={(e) => setRepeat(e.target.checked)} /> Repetir cada semana
            </label>
            {repeat ? (
              <>
                <Field label="¿Cuántas semanas?" type="number" min={1} max={60} value={weeks} onChange={(e) => setWeeks(Number(e.target.value) || 1)} />
                {!Number.isNaN(start.getTime()) ? (
                  <p className="small">
                    Todos los {WEEKDAYS[start.getDay()]} a las {start.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}, {count} semanas.
                  </p>
                ) : null}
              </>
            ) : null}
          </>
        ) : null}
        <Field label="Lugar (opcional)" name="place" maxLength={200} defaultValue={rehearsal?.place ?? ''} placeholder="Templo, salón de música…" />
        {!repeat ? (
          <div className="field">
            <label htmlFor="rh-service">¿Para qué culto se ensaya? (opcional)</label>
            <SearchSelect
              id="rh-service"
              name="serviceId"
              defaultValue={rehearsal?.service?.id ?? ''}
              emptyLabel="Ninguno en especial"
              options={(services.status === 'ready' ? services.data : []).map((s) => ({ value: s.id, label: `${s.title} · ${formatDayHeading(new Date(s.startsAt))}` }))}
            />
            <small className="field__hint">Así, el ensayo usa los mismos cantos del culto.</small>
          </div>
        ) : null}
        <div className="field">
          <label htmlFor="rh-notes">Notas (opcional)</label>
          <textarea id="rh-notes" name="notes" rows={2} maxLength={2000} defaultValue={rehearsal?.notes ?? ''} />
        </div>
        <ConvocationFields idPrefix="rh" ministry="Alabanza" people={people} audience={convocation.audience} chosen={convocation.chosen} onChange={setConvocation} />
        <div className="actions">
          <Button type="submit" busy={busy}>
            {rehearsal ? 'Guardar cambios' : repeat ? `Crear ${count} ensayos` : 'Crear ensayo'}
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Card>
  );
}

/** Un ensayo: cuándo, quiénes y sus cantos (con letra). Si es para un culto, son los cantos de ese culto. */
export function RehearsalPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const { rehearsalId = '' } = useParams();
  const base = `/worship/churches/${churchId}/rehearsals/${rehearsalId}`;
  const data = useAsync((signal) => (churchId ? api.get<RehearsalDetail>(base, signal) : Promise.resolve(null)), [base]);
  const [mode, setMode] = useState<'view' | 'edit' | 'songs'>('view');
  const [msg, setMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;
  if (data.status === 'loading') return <div className="page"><Loading /></div>;
  if (data.status === 'error') return <div className="page"><ErrorState error={data.error} onRetry={data.reload} /></div>;
  const r = data.data;
  if (!r) return null;

  async function run(action: () => Promise<unknown>, success: string) {
    setBusy(true);
    setMsg(null);
    try {
      await action();
      setMsg({ tone: 'success', text: success });
      data.reload();
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page page--narrow">
      <p className="small">
        <Link to="/app/ensayos">← Ensayos</Link>
      </p>
      <header className="page__header">
        <h1>{r.title}</h1>
        <p className="muted">
          {when(r)}
          {r.place ? ` · ${r.place}` : ''}
          {r.canceled ? ' · Cancelado' : ''}
        </p>
      </header>
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}

      {mode === 'edit' ? (
        <RehearsalForm
          churchId={churchId}
          people={r.people}
          rehearsal={r}
          onCancel={() => setMode('view')}
          onSaved={() => {
            setMode('view');
            setMsg({ tone: 'success', text: 'Ensayo guardado.' });
            data.reload();
          }}
        />
      ) : (
        <Card
          title="Datos"
          action={
            r.canManage ? (
              <span className="actions">
                <Button variant="ghost" onClick={() => setMode('edit')}>
                  Editar
                </Button>
                <Button
                  variant="ghost"
                  disabled={busy}
                  onClick={() => {
                    if (window.confirm('¿Cancelar este ensayo?')) void run(() => api.post(`${base}/cancel`), 'Ensayo cancelado.');
                  }}
                >
                  Cancelar ensayo
                </Button>
              </span>
            ) : null
          }
        >
          <dl className="details">
            <dt>Convocados</dt>
            <dd>
              {audienceLabel(r.audience, 'Alabanza', r.convoked.length)}
              {r.audience !== 'iglesia' && r.convoked.length ? <span className="small muted"> · {r.convoked.map((c) => c.name).join(', ')}</span> : null}
            </dd>
            {r.service ? (
              <>
                <dt>Para el culto</dt>
                <dd>
                  <Link to={`/app/cultos/${r.service.id}?ver=alabanza`}>
                    {r.service.title} · {formatDayHeading(new Date(r.service.startsAt))}
                  </Link>
                </dd>
              </>
            ) : null}
            {r.notes ? (
              <>
                <dt>Notas</dt>
                <dd className="pre-line">{r.notes}</dd>
              </>
            ) : null}
          </dl>
        </Card>
      )}

      {mode === 'songs' ? (
        <SetlistEditor
          churchId={churchId}
          url={`${base}/songs`}
          title={r.service ? `Cantos de «${r.service.title}»` : 'Cantos del ensayo'}
          initial={r.songs.map((s) => ({ songId: s.songId, title: s.title, key: s.key ?? '', notes: s.notes ?? '' }))}
          {...(r.service ? { hint: 'Son los mismos cantos del culto: lo que cambies aquí se ve también en el culto.' } : {})}
          onDone={(saved) => {
            setMode('view');
            if (saved) {
              setMsg({ tone: 'success', text: 'Cantos guardados.' });
              data.reload();
            }
          }}
        />
      ) : (
        <Card
          title={`Cantos (${r.songs.length})`}
          action={
            r.canManage ? (
              <Button variant="secondary" onClick={() => setMode('songs')}>
                {r.songs.length ? 'Cambiar cantos' : 'Elegir cantos'}
              </Button>
            ) : null
          }
        >
          {r.service ? <p className="small muted">Son los cantos del culto «{r.service.title}»: lo que se cambie aquí o en el culto se ve en los dos.</p> : null}
          {r.songs.length === 0 ? <p className="muted">Todavía no se eligen los cantos.</p> : null}
          <ol className="program-songs">
            {r.songs.map((s, i) => (
              <li key={`${s.songId}-${i}`}>
                <details>
                  <summary>
                    <strong>{s.title}</strong>
                    {s.notes ? <span className="badge">{s.notes}</span> : null}
                    <span className="small muted">
                      {s.key ? ` Tono ${s.key}` : ''}
                      {s.author ? ` · ${s.author}` : ''}
                    </span>
                  </summary>
                  {s.lyrics ? <p className="pre-line program-songs__lyrics">{s.lyrics.replace(/^\[(.+)\]$/gm, '— $1 —')}</p> : <p className="muted small">Sin letra registrada.</p>}
                </details>
              </li>
            ))}
          </ol>
          {r.canManage && r.songs.length && !r.service ? <ToService churchId={churchId} base={base} rehearsal={r} busy={busy} run={run} /> : null}
        </Card>
      )}
    </div>
  );
}

/** Un ensayo sin culto ligado puede pasar sus cantos a un culto (reemplaza los cantos de ese culto). */
function ToService({ churchId, base, rehearsal: r, busy, run }: { churchId: string; base: string; rehearsal: RehearsalDetail; busy: boolean; run: (action: () => Promise<unknown>, success: string) => Promise<void> }) {
  const services = useAsync((signal) => api.get<{ items: WorshipService[] }>(`/worship/churches/${churchId}/services`, signal).then((x) => x.items.filter((s) => s.status === 'planned')), [churchId]);
  const [pick, setPick] = useState('');
  const target = services.status === 'ready' ? services.data.find((s) => s.id === pick) : undefined;
  function send() {
    if (!target) return;
    if (!window.confirm(`Los cantos de «${target.title}» se reemplazan por estos ${r.songs.length}. ¿Continuar?`)) return;
    void run(() => api.post(`${base}/to-service`, { serviceId: target.id }), `Listo: los cantos ya están en «${target.title}».`);
  }
  return (
    <div className="to-service">
      <div className="field">
        <label htmlFor="rh-to-service">Pasar estos cantos al culto</label>
        <SearchSelect
          id="rh-to-service"
          value={pick}
          onChange={setPick}
          placeholder="Elige el culto…"
          options={(services.status === 'ready' ? services.data : []).map((s) => ({ value: s.id, label: `${s.title} · ${formatDayHeading(new Date(s.startsAt))}` }))}
        />
        <small className="field__hint">Si el ensayo es para un culto, mejor elígelo en «Editar»: así ensayo y culto comparten los mismos cantos.</small>
      </div>
      <Button variant="secondary" busy={busy} disabled={!target} onClick={send}>
        Pasar los cantos
      </Button>
    </div>
  );
}
